import React, { useEffect, useRef } from "react";
import { Form } from "antd";
import { useDebouncedCallback } from "use-debounce";
import type { ChatbotFormValues } from "../types";
import { ChatbotSettingsFields } from "./ChatbotSettingsFields";

type FunctionalSettings = ChatbotFormValues["functional_settings"];

/** Coalesces a run of toggles into one request. */
const SAVE_DELAY = 600;

interface ChatbotSettingsFormProps {
  initialValues: ChatbotFormValues;
  /** Resolves false when the save failed, so the form can revert. */
  onSave: (settings: FunctionalSettings) => Promise<boolean>;
}

/** Chat settings on the edit page, saved as they change — there's no save
 *  button. Each change sends the whole branch, which is what the PATCH
 *  replaces. */
export const ChatbotSettingsForm: React.FC<ChatbotSettingsFormProps> = ({
  initialValues,
  onSave,
}) => {
  const [form] = Form.useForm<ChatbotFormValues>();
  const saved = useRef<FunctionalSettings>(initialValues.functional_settings);
  const mounted = useRef(true);

  const save = useDebouncedCallback(
    async (changed: Partial<FunctionalSettings>) => {
      const previous = saved.current;
      // The form reports only the fields it renders, and the PATCH replaces
      // the branch — so merge, or settings this card doesn't show get wiped.
      const next = { ...previous, ...changed };
      if (await onSave(next)) {
        saved.current = next;
        return;
      }
      // Detached form: nothing to revert, and setFieldsValue would warn.
      if (mounted.current) {
        form.setFieldsValue({ functional_settings: previous });
      }
    },
    SAVE_DELAY
  );

  // use-debounce drops a pending call on unmount, and the card unmounts on
  // every rail switch — flush, or the last toggle silently never saves. Set on
  // the way in too: a remount reuses the ref, and a stale `false` would leave
  // the revert below permanently dead.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      void save.flush();
    };
  }, [save]);

  return (
    <Form<ChatbotFormValues>
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onValuesChange={(_changed, values) => save(values.functional_settings)}
    >
      <ChatbotSettingsFields variant="edit" />
    </Form>
  );
};
